import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import { FC, useEffect, useState } from 'react'
import mclasses from '../../styles/modal.module.css'
import iclasses from '../../styles/input.module.css'
import classes from './index.module.css'
import { IcoRemove } from '@/utils/icons'
import cn from 'classnames'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { Field, Lead, RequestCreateLead } from '@/api/types/crm.types'
import { CheckBoxLabel } from '@UI/CheckBox'
import VariableLeadField from '../VariableLeadField/VariableLeadField'
import Select from '../base/Select/Select'
import { ignoreLeadFields, validateForm } from './validateLeadData'
import { useCRMContext } from '../../CRMContext'

interface CreateOneModalContentProps {
  defData?: {
    NAME?: string | null
    PHONE?: string | null
    EMAIL?: string | null
    PARENTNAME?: string | null
    PARENTEMAIL?: string | null
    PARENTPHONE?: string | null
  }
  onClose: () => void
  onSuccess: (
    lead: Pick<Lead, 'id' | 'fields' | 'status_id'> | null | undefined,
  ) => void
}

const CreateOneModalContent: FC<CreateOneModalContentProps> = ({
  defData,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const {
    preloadingData: { fields, managers },
  } = useCRMContext()

  const [formData, setFormData] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!defData) return
    const filteredDefData = Object.entries(defData).reduce(
      (acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, string>,
    )
    setFormData((prev) => ({ ...prev, ...filteredDefData }))
  }, [defData])

  const [addManagerId, setAddManagerId] = useState<false | null | number>(false)

  const handleChange = (uniqueName: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [uniqueName]: value,
    }))
    setErrors((prev) => ({ ...prev, [uniqueName]: '' }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const resultValidation = validateForm(formData, fields, false)
    if (resultValidation !== true) {
      setErrors(resultValidation)
      return
    }

    setLoading(true)

    const requestData: RequestCreateLead = {
      fields: fields
        .filter((field) => field.is_active)
        .map((field) => ({
          field_id: field.id,
          value: formData[field.unique_name] || '',
        })),
      manager_id:
        ((typeof addManagerId === 'number' ||
          typeof addManagerId === 'string') &&
          addManagerId) ||
        null,
    }

    crmApi
      .insertLead(requestData)
      .then((res) => {
        if (isError(res)) throw new Error()
        const dataR = res?.data?.data as any

        addAlert({
          title: 'Успех',
          text: dataR?.message || 'Лид успешно добавлен',
          type: 'success',
        })

        setFormData({})
        setErrors({})
        onSuccess(dataR?.lead)
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка при добавлении лида',
          type: 'error',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const renderField = (field: Field, error: string) => {
    const fieldProps = {
      id: field.unique_name,
      name: field.unique_name,
      value: formData[field.unique_name] ?? '',
      onChange: (
        e:
          | React.ChangeEvent<
              HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >
          | string,
      ) =>
        handleChange(
          field.unique_name,
          typeof e === 'string' ? e : e.target.value,
        ),
      error,
      placeholder: field.type === 'phone_number' ? field.name : undefined,
    }

    return (
      <VariableLeadField
        type={field.type}
        fieldProps={{ field, ...fieldProps }}
      />
    )
  }

  return (
    <>
      <div
        className={cn(
          mclasses.formWrapper,
          mclasses.overflowAuto,
          mclasses.gapXL,
          'custom-scroll',
        )}
      >
        {fields
          .filter(
            (f) => f.is_active && !ignoreLeadFields.includes(f.unique_name),
          )
          .map((field) => (
            <div key={field.id}>
              <label htmlFor={field.unique_name} className={mclasses.label}>
                {field.name}
              </label>
              {renderField(field, errors[field.unique_name] ?? '')}
            </div>
          ))}
      </div>
      <CheckBoxLabel
        className={classes.checkBoxLabel}
        value={!(addManagerId === false)}
        onClick={() =>
          setAddManagerId((prev) => (prev === false ? null : false))
        }
        label="Выбрать менеджера (перевести в работу)"
      />
      {addManagerId !== false && (
        <Select
          showSearch
          placeholder="Выберите менеджера"
          options={managers.map((manager) => ({
            label: manager.fio,
            value: manager.id,
          }))}
          onChange={setAddManagerId}
          value={addManagerId}
        />
      )}
      <div className={mclasses.actions}>
        <Button
          className={mclasses.marginLeftAuto}
          disabled={loading}
          onClick={handleSubmit}
        >
          Добавить
        </Button>
      </div>
    </>
  )
}

const CreateOneModalContentFull: FC<CreateOneModalContentProps> = ({
  onClose,
  ...props
}) => {
  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Добавить Лида</span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      <CreateOneModalContent onClose={onClose} {...props} />
    </div>
  )
}

export default CreateOneModalContentFull
export {
  CreateOneModalContent as CreateOneModalContentClear,
  CreateOneModalContentFull,
}
